import type { MouseEvent } from 'react'
import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Plus, X } from 'lucide-react'
import { ProductVariantColor_ } from 'src/api/db/apiProductVariantColor'
import { useProductsOpenApi } from 'src/api/db/apiProductsOpen'
import { useQuoteItemApi } from 'src/api/db/apiQuoteItem'
import { queryKeys } from 'src/api/db/queryKeys'
import { ensureQuoteItemCanvasStub } from 'src/api/db/utils/ensureQuoteItemCanvasStub'
import { useSupabaseClient } from 'src/api/supabase'
import { ProductImage } from 'src/shared/components/ProductImage'
import { ProductPreview } from 'src/shared/components/ProductGrid/ProductCard/ProductPreview'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button, Tooltip } from 'src/shared/components/ui'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useOrganizationCurrency } from 'src/shared/hooks/useOrganizationCurrency'
import { cn } from 'src/shared/components/ui/utils'

export const InlineProductChip = ({
  product_id,
  product_variant_color_id,
  name,
  quote_id,
  id,
  variant = 'inline',
  note,
}: {
  product_id: number
  product_variant_color_id: number
  name: string
  quote_id: number
  /** When set, anchors onboarding tooltip to this chip */
  id?: string
  variant?: 'inline' | 'row'
  note?: string
}) => {
  const { t } = useLocalization()
  const currency = useOrganizationCurrency()
  const [previewOpen, setPreviewOpen] = useState(false)
  const togglingRef = useRef(false)
  const canAddToOrder = quote_id > 0
  const supabase = useSupabaseClient()
  const queryClient = useQueryClient()

  const { createQuoteItem, deleteQuoteItem, quoteItems } = useQuoteItemApi({
    quote_id: canAddToOrder ? quote_id : undefined,
  })
  const { productsByIdsOpen: products } = useProductsOpenApi({
    product_id,
  })
  const product = products?.data?.[0] as any

  const productName = t(product?.item_name_translations)
  const price = product?.price
  const product_variant = product?.product_variant?.[0]
  const product_variant_colors =
    product_variant?.product_variant_color as ProductVariantColor_[]
  const chosen_product_variant_color =
    product_variant_colors?.find(
      (pvc) => pvc.id === product_variant_color_id
    ) || product_variant_colors?.[0]
  const image_url = chosen_product_variant_color?.product_image?.sort(
    (a, b) => (a?.order ?? 0) - (b?.order ?? 0)
  )[0]?.image_url

  const inOrderQuoteItem = canAddToOrder
    ? quoteItems.data?.find((item) => item.product_id === product_id)
    : null
  const inOrder = !!inOrderQuoteItem

  const handleOpenPreview = (e: MouseEvent) => {
    e.stopPropagation()
    if (product?.id) setPreviewOpen(true)
  }

  const handleToggleOrder = async (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (
      togglingRef.current ||
      createQuoteItem.isPending ||
      deleteQuoteItem.isPending
    ) {
      return
    }

    togglingRef.current = true

    try {
      if (inOrder && inOrderQuoteItem) {
        await deleteQuoteItem.mutateAsync({
          id: inOrderQuoteItem.id,
          quote_id,
        })
        return
      }

      const result = await createQuoteItem.mutateAsync({
        product_id,
        product_variant_color_id,
        quantity: 1,
        quote_id,
        type: 'product',
        unit_price: price ?? 0,
        description: productName || name,
      })

      const quoteItemId = result.data?.id
      if (quoteItemId) {
        await ensureQuoteItemCanvasStub(supabase, {
          quoteItemId,
          quantity: 1,
          decorationDescription: t('New decoration', 'Ny dekoration'),
        })
        void queryClient.invalidateQueries({
          queryKey: [queryKeys.quoteItems, quote_id],
        })
      }
    } catch (error) {
      console.error('Error adding product to quote:', error)
    } finally {
      togglingRef.current = false
    }
  }

  const addButton = canAddToOrder && (
    <Tooltip
      delayDuration={100}
      content={
        inOrder
          ? t('Remove from order', 'Ta bort')
          : t('Add to order', 'Lägg till i order')
      }
    >
      <span className="inline-flex">
        <Button
          type="button"
          variant={inOrder ? 'destructive' : 'green'}
          radius="full"
          className={cn(
            'w-7 h-7 p-0',
            variant === 'inline' && 'hidden group-hover/chip:flex'
          )}
          onClick={handleToggleOrder}
          disabled={createQuoteItem.isPending || deleteQuoteItem.isPending}
          aria-label={
            inOrder
              ? t('Remove from order', 'Ta bort')
              : t('Add to order', 'Lägg till')
          }
        >
          {inOrder ? (
            <X className="w-3.5 h-3.5" strokeWidth={2.5} />
          ) : (
            <Plus className="w-3.5 h-3.5" />
          )}
        </Button>
      </span>
    </Tooltip>
  )

  const preview = product && chosen_product_variant_color && (
    <ProductPreview
      open={previewOpen}
      setOpen={setPreviewOpen}
      chosen_product_variant_color={chosen_product_variant_color}
      product_variant_colors={product_variant_colors || []}
      product_name={productName || name}
      product_description={
        product?.item_description_translations
          ? t(product.item_description_translations)
          : ''
      }
      moq={product?.moq || 1}
      price={price || 0}
      article_number={product?.article_number || ''}
      product_id={product.id}
      klayder_recommends={product?.klayder_recommends ?? false}
      klayder_recommendation_text={
        product?.klayder_recommendation_text ?? ''
      }
      related_product_ids={product?.related_product_ids}
      product_categories={product?.category_product}
      addToCurrentQuoteId={quote_id}
    />
  )

  if (variant === 'row') {
    return (
      <Row
        role="listitem"
        className="w-full items-center gap-3 px-3 py-2 group/chip hover:bg-muted/40"
        id={id}
      >
        <div
          className={cn(
            'w-10 h-10 shrink-0 bg-white border border-gray-200 rounded-md p-0.5 cursor-pointer',
            inOrder && 'border-emerald-500'
          )}
          onClick={handleOpenPreview}
        >
          <ProductImage
            image_url={image_url}
            containerClassName="w-full h-full rounded-md [&_img]:object-cover"
          />
        </div>

        <Column className="min-w-0 flex-1 gap-0">
          <Tooltip content={t('View product', 'Visa produkt')} delayDuration={100}>
            <span
              role="button"
              tabIndex={0}
              className="block min-w-0 truncate text-sm font-medium cursor-pointer group-hover/chip:underline"
              onClick={handleOpenPreview}
              onKeyDown={(e) =>
                e.key === 'Enter' && product?.id && setPreviewOpen(true)
              }
              aria-label={t('View product details', 'Visa produktdetaljer')}
            >
              {name}
            </span>
          </Tooltip>
          {note ? (
            <span className="block min-w-0 truncate text-xs text-muted-foreground">
              {note}
            </span>
          ) : null}
        </Column>

        {price != null && (
          <span className="shrink-0 text-xs text-muted-foreground">
            {price.toLocaleString()} {currency}
          </span>
        )}

        {addButton}
        {preview}
      </Row>
    )
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 align-middle p-1 group/chip'
      )}
      id={id}
    >
      <div
        className={cn(
          'w-7 h-7 bg-white border border-gray-200 rounded-full p-[3px] group-hover/chip:hidden transition-all duration-200',
          inOrder && 'border-emerald-500'
        )}
      >
        <ProductImage
          image_url={image_url}
          containerClassName="w-full h-full rounded-full [&_img]:object-cover"
        />
      </div>

      {addButton}

      <Tooltip content={t('View product', 'Visa produkt')} delayDuration={100}>
        <span
          role="button"
          tabIndex={0}
          className="font-normal text-sm mb-[3px] cursor-pointer group-hover/chip:underline"
          onClick={handleOpenPreview}
          onKeyDown={(e) =>
            e.key === 'Enter' && product?.id && setPreviewOpen(true)
          }
          aria-label={t('View product details', 'Visa produktdetaljer')}
        >
          {name}
        </span>
      </Tooltip>

      {preview}
    </span>
  )
}
